/**
 * PATH: packages/email-hub/src/feature/components/BulkLinkToJobModal.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-BULKLINKTOJOBMODAL-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/BulkLinkToJobModal.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/BulkLinkToJobModal.tsx:
 *     thin re-export shim that points back here.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { Briefcase, Link2, Loader2, MapPin, Search, Sparkles, X } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { allTokensMatchFields, tokenizeSearchQuery } from '@quotech/ui'
import { useQuery } from '@tanstack/react-query'
import api from '../../_internal/hooks/useApi'

interface BulkLinkToJobModalProps {
  emailIds: string[]
  /** Used purely as a hint for local scoring — the backend doesn't
   *  see this. Pass the subject of the most recently selected row. */
  hintSubject?: string
  hintFrom?: string
  isOpen: boolean
  onClose: () => void
  /** Fired after the bulk link succeeds. ``meta`` carries the extra
   *  cross-mailbox propagation + auto-contact-registration counts the
   *  backend now reports — see organisation/services/email_job_linking
   *  in quotech-backend (qb!2436). The parent surfaces them in the
   *  success toast so the operator understands why the count went up
   *  beyond the explicit selection. */
  onLinked?: (
    jobId: number,
    count: number,
    meta?: {
      propagatedSiblings: number
      contactsRegistered: number
      primaryEmailSet: boolean
    },
  ) => void
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 2)
}

function scoreJobMatch(job: any, subjectTokens: string[], fromAddr: string): number {
  let score = 0
  const jobNum = (job.job_number || '').toLowerCase()
  const client = (job.client_name || '').toLowerCase()
  const address = (job.site_address || '').toLowerCase()
  const subjectStr = subjectTokens.join(' ')

  if (jobNum && subjectStr.includes(jobNum)) score += 50
  for (const part of address.split(/[\s,]+/).filter((p: string) => p.length > 2)) {
    if (subjectTokens.includes(part)) score += 15
  }
  for (const part of client.split(/[\s,]+/).filter((p: string) => p.length > 2)) {
    if (subjectTokens.includes(part)) score += 12
    if (fromAddr && fromAddr.toLowerCase().includes(part)) score += 10
  }
  return score
}

export default function BulkLinkToJobModal({
  emailIds,
  hintSubject,
  hintFrom,
  isOpen,
  onClose,
  onLinked,
}: BulkLinkToJobModalProps) {
  const [search, setSearch] = useState('')
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null)
  // Contract envelope routing — operator request 2026-05-10. When the
  // operator picks a job that's part of an envelope (parent OR child),
  // a second small dropdown appears so they can route the emails to a
  // sibling/parent without going back to the search and starting over.
  // Defaults to whatever they picked. Cleared when the picked job
  // changes (so picking a different job from the search doesn't carry
  // a stale sibling target). Null = use selectedJobId as-is.
  const [envelopeTargetId, setEnvelopeTargetId] = useState<number | null>(null)
  const [linking, setLinking] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.get('/jobs').then(r => r.data),
    enabled: isOpen,
  })

  // Resolve the envelope membership of the currently-picked job from
  // the same jobs list we already have (no extra API hop). A row is
  // an envelope member when EITHER it has a parent (it's a child) OR
  // it's the parent of at least one row in the list.
  const envelopeMembers = useMemo(() => {
    if (!selectedJobId) return [] as any[]
    const picked = jobs.find((j: any) => j.id === selectedJobId)
    if (!picked) return []
    // Resolve the contract root: the picked row itself if it's a
    // parent, else its parent_job_id.
    const rootId = picked.parent_job_id ?? picked.id
    const root = jobs.find((j: any) => j.id === rootId)
    if (!root) return []
    const children = jobs.filter((j: any) => j.parent_job_id === rootId)
    if (children.length === 0 && picked.parent_job_id == null) {
      // Standalone — no envelope, no sub-picker.
      return []
    }
    // De-dup; root first, then children in id order.
    return [root, ...children]
  }, [jobs, selectedJobId])

  // Reset envelope target whenever the operator picks a different job
  // from the search results. Default to the picked id.
  useEffect(() => {
    setEnvelopeTargetId(selectedJobId)
  }, [selectedJobId])

  const subjectTokens = useMemo(() => tokenize(hintSubject || ''), [hintSubject])

  const filtered = useMemo(() => {
    let results: any[]
    // W4a — canonical AND-of-tokens matching (shared with the Cmd+K
    // palette). Field selection is unchanged.
    const queryTokens = tokenizeSearchQuery(search)
    if (queryTokens.length > 0) {
      results = jobs.filter((j: any) =>
        allTokensMatchFields(
          [j.job_number, j.client_name, j.site_address, j.scope],
          queryTokens,
        ),
      )
    } else {
      results = [...jobs]
    }
    const scored = results.map((j: any) => ({
      job: j,
      score: scoreJobMatch(j, subjectTokens, hintFrom || ''),
    }))
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return String(b.job.job_number).localeCompare(String(a.job.job_number), undefined, { numeric: true })
    })
    return scored.slice(0, 50)
  }, [jobs, search, subjectTokens, hintFrom])

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
      setSelectedJobId(null)
      setErr(null)
    }
  }, [isOpen])

  const handleLink = useCallback(async () => {
    if (!selectedJobId || emailIds.length === 0) return
    // Effective target = envelope dropdown choice (when the picked
    // job is in an envelope) or the search-picked id directly.
    const finalJobId = envelopeTargetId ?? selectedJobId
    setLinking(true)
    setErr(null)
    try {
      const { emailApi } = await import('../lib/api')
      const result = await emailApi<{
        success: boolean
        affected: number
        propagated_siblings?: number
        contacts_registered?: number
        primary_email_set?: boolean
      }>('/api/emails/bulk/', {
        method: 'POST',
        body: JSON.stringify({
          email_ids: emailIds,
          action: 'link_job',
          job_id: finalJobId,
        }),
      })
      onLinked?.(finalJobId, result?.affected ?? emailIds.length, {
        propagatedSiblings: result?.propagated_siblings ?? 0,
        contactsRegistered: result?.contacts_registered ?? 0,
        primaryEmailSet: Boolean(result?.primary_email_set),
      })
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to link emails')
    } finally {
      setLinking(false)
    }
  }, [selectedJobId, envelopeTargetId, emailIds, onLinked, onClose])

  if (!isOpen) return null

  const topScore = filtered[0]?.score ?? 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-xl shadow-2xl w-[560px] max-h-[640px] flex flex-col animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            <h2 className="font-semibold text-foreground">
              Link {emailIds.length} email{emailIds.length === 1 ? '' : 's'} to Job
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="px-4 pt-3 pb-2">
          <p className="text-xs text-muted-foreground mb-3">
            All {emailIds.length} selected email{emailIds.length === 1 ? ' will be' : 's will be'} allocated to the job you pick below.
            Existing job allocations on these emails will be overwritten.
          </p>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search jobs by number, client, address, or scope..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-2 min-h-0">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No jobs found</p>
          ) : (
            <div className="space-y-1">
              {filtered.map(({ job, score }: { job: any; score: number }) => (
                <button
                  key={job.id}
                  onClick={() => setSelectedJobId(job.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                    selectedJobId === job.id
                      ? 'bg-primary/10 border border-primary/30'
                      : 'hover:bg-accent border border-transparent',
                  )}
                >
                  <Briefcase
                    className={cn(
                      'h-4 w-4 shrink-0 mt-0.5',
                      selectedJobId === job.id ? 'text-primary' : 'text-muted-foreground',
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-foreground">{job.job_number}</span>
                      {job.client_name && (
                        <span className="text-xs text-muted-foreground truncate">{job.client_name}</span>
                      )}
                      {score > 0 && topScore > 0 && score === topScore && (
                        <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">
                          <Sparkles className="h-2.5 w-2.5" />Best match
                        </span>
                      )}
                    </div>
                    {job.site_address && (
                      <p className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="truncate">{job.site_address}</span>
                      </p>
                    )}
                    {job.scope && (
                      <p className="text-[11px] text-muted-foreground/70 truncate mt-0.5">{job.scope}</p>
                    )}
                  </div>
                  {job.status && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                      {job.status}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Contract envelope sub-picker — only renders when the
            picked job is part of an envelope. Lets the operator
            route into a specific sub-job (e.g. picked 1309 → route
            to .01) without going back to the search. Defaults to
            whatever they picked so the obvious case is zero clicks. */}
        {envelopeMembers.length > 0 && (
          <div className="px-4 pt-3 pb-3 border-t border-border bg-primary/[0.03]">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-primary/80">
                Allocate to
              </span>
              <span className="text-[10px] text-muted-foreground">
                · Contract envelope detected - pick the exact sub-job
              </span>
            </div>
            <select
              value={envelopeTargetId ?? selectedJobId ?? ''}
              onChange={(e) => setEnvelopeTargetId(Number(e.target.value))}
              className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground focus:border-primary focus:outline-none cursor-pointer"
            >
              {envelopeMembers.map((m: any) => (
                <option key={m.id} value={m.id}>
                  {m.job_number}
                  {m.scope_label ? ` - ${m.scope_label}` : ''}
                  {m.id === selectedJobId ? ' (picked)' : ''}
                  {m.id === (selectedJobId && jobs.find((j: any) => j.id === selectedJobId)?.parent_job_id) ? ' - parent / universal' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {err && (
          <p className="px-4 py-2 text-xs text-destructive border-t border-border">{err}</p>
        )}

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleLink}
            disabled={!selectedJobId || linking}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {linking
              ? 'Linking...'
              : (() => {
                  const target = envelopeTargetId ?? selectedJobId
                  const targetLabel = jobs.find((j: any) => j.id === target)?.job_number
                  return `Link ${emailIds.length} email${emailIds.length === 1 ? '' : 's'}${targetLabel ? ` to ${targetLabel}` : ''}`
                })()
            }
          </button>
        </div>
      </div>
    </div>
  )
}
